<template>
  <div class="col-sm-12 delivery-edit-page">
    <div class="edit-container">
      <!-- =====================================================
           PAGE HEADER
      ====================================================== -->
      <div class="page-title-card">
        <div>
          <h5>Delivery Schedule Edit</h5>

          <span>
            Dealer assignment and schedule status can be edited from this page.
          </span>
        </div>

        <button type="button" class="back-button" @click="goBack">
          <i class="fa fa-arrow-left"></i>
          Back
        </button>
      </div>

      <div v-if="loading" class="loading-card">
        <span class="spinner-border spinner-border-sm"></span>
        Loading delivery schedule...
      </div>

      <template v-else>
        <!-- =====================================================
             SECTION 1: READONLY SCHEDULE DETAILS
        ====================================================== -->
        <div class="section-card">
          <div class="section-header">
            <div>
              <h6>Schedule Details</h6>

              <span>
                Schedule details are locked. Status and dealer list can be
                changed.
              </span>
            </div>

            <div class="schedule-status-box">
              <div class="schedule-status-text">
                <span
                  class="status-dot"
                  :class="{ active: isScheduleActive }"
                ></span>

                <div>
                  <strong>
                    {{
                      isScheduleActive
                        ? "Active Schedule"
                        : "Inactive Schedule"
                    }}
                  </strong>

                  <small>
                    {{
                      isScheduleActive
                        ? "Deliveries are enabled"
                        : "Deliveries are stopped"
                    }}
                  </small>
                </div>
              </div>

              <button
                type="button"
                class="schedule-switch"
                :class="{ active: isScheduleActive }"
                :disabled="saving"
                :aria-pressed="isScheduleActive ? 'true' : 'false'"
                @click="toggleScheduleStatus"
              >
                <span class="switch-label">
                  {{ isScheduleActive ? "ON" : "OFF" }}
                </span>

                <span class="switch-knob"></span>
              </button>
            </div>
          </div>

          <div class="details-grid">
            <div class="readonly-field compact-field">
              <label>Schedule Name</label>

              <div class="readonly-value">
                {{ schedule.name || "-" }}
              </div>
            </div>

            <div class="readonly-field compact-field">
              <label>Distributor</label>

              <div class="readonly-value">
                {{ schedule.distributor_name || "-" }}
              </div>
            </div>

            <div class="readonly-field wide-field">
              <label>Route</label>

              <div
                class="readonly-value readonly-long-value route-value"
                :title="schedule.route_name || '-'"
              >
                {{ schedule.route_name || "-" }}
              </div>
            </div>

            <div class="readonly-field wide-field">
              <label>Vehicle</label>

              <div
                class="readonly-value readonly-long-value"
                :title="schedule.vehicle_name || '-'"
              >
                {{ schedule.vehicle_name || "-" }}
              </div>
            </div>

            <div class="readonly-field compact-field">
              <label>Time Slot</label>

              <div class="readonly-value">
                {{ schedule.time_slot_label || "-" }}
              </div>
            </div>

            <div class="readonly-field compact-field">
              <label>Effective From</label>

              <div class="readonly-value">
                {{ formatDate(schedule.effective_from) }}
              </div>
            </div>

            <div class="readonly-field compact-field">
              <label>Effective To</label>

              <div class="readonly-value">
                {{ formatDate(schedule.effective_to) }}
              </div>
            </div>

            <div class="readonly-field wide-field">
              <label>Remarks</label>

              <div
                class="readonly-value readonly-long-value"
                :title="schedule.remarks || '-'"
              >
                {{ schedule.remarks || "-" }}
              </div>
            </div>
          </div>
        </div>

        <!-- =====================================================
             SECTION 2: DATE SET
        ====================================================== -->
        <div class="section-card date-section">
          <div class="section-header">
            <div>
              <h6>Delivery Date Set</h6>

              <span>
                Current schedule date set. This cannot be edited here.
              </span>
            </div>
          </div>

          <div class="date-preview-card">
            <div class="date-preview-icon">
              <i class="fa fa-calendar"></i>
            </div>

            <div class="date-preview-content">
              <span
                class="batch-type"
                :class="getBatchTypeClass(schedule.schedule_type)"
              >
                {{
                  schedule.schedule_type_label ||
                  getFrequencyLabel(schedule.schedule_type)
                }}
              </span>

              <strong>
                {{ schedule.schedule_text || "-" }}
              </strong>

              <small>
                {{ schedule.time_slot_label || "-" }}
              </small>
            </div>
          </div>
        </div>

        <!-- =====================================================
             SECTION 3: DEALER LIST EDIT
        ====================================================== -->
        <div class="section-card">
          <div class="section-header dealer-section-header">
            <div>
              <h6>Edit Dealer List</h6>

              <span>
                Existing dealers are selected and shown first. Available dealers
                can be added to this schedule.
              </span>
            </div>

            <div class="section-count">
              {{ selectedDealerIds.length }} selected
            </div>
          </div>

          <div class="dealer-filter-grid">
            <div class="form-item">
              <label>Search Dealer</label>

              <div class="search-input-wrap">
                <i class="fa fa-search"></i>

                <input
                  type="text"
                  v-model="dealerSearchText"
                  placeholder="Search dealer by name, route, address or schedule..."
                  :disabled="saving"
                />
              </div>
            </div>
          </div>

          <div class="selection-table-card">
            <div class="selection-table-toolbar">
              <div class="toolbar-title">
                <strong>Dealers</strong>

                <span>
                  {{ filteredDealerList.length }}
                  {{ filteredDealerList.length === 1 ? "dealer" : "dealers" }}
                </span>
              </div>

              <div class="toolbar-actions">
                <button
                  type="button"
                  class="mini-action-button"
                  :disabled="!selectableFilteredDealerList.length || saving"
                  @click="selectAllVisibleDealers"
                >
                  Select visible
                </button>

                <button
                  type="button"
                  class="mini-action-button clear"
                  :disabled="!selectedDealerIds.length || saving"
                  @click="clearSelectedDealers"
                >
                  Clear
                </button>
              </div>
            </div>

            <div class="table-scroll dealer-table-scroll">
              <table class="selection-table dealer-table">
                <thead>
                  <tr>
                    <th class="select-column">
                      <input
                        type="checkbox"
                        :checked="isAllDealersSelected"
                        :disabled="!selectableFilteredDealerList.length || saving"
                        @change="toggleAllDealers"
                      />
                    </th>

                    <th>Dealer Name</th>
                    <th>Route</th>
                    <th>Address</th>
                    <th>Status</th>
                    <th>Existing Schedules</th>
                  </tr>
                </thead>

                <tbody>
                  <tr
                    v-for="dealer in filteredDealerList"
                    :key="dealer.row_key"
                    :class="{
                      selected:
                        dealer.id &&
                        selectedDealerIds.includes(String(dealer.id)),
                      'existing-row': dealer.is_existing,
                      'disabled-row': !canSelectDealer(dealer),
                    }"
                  >
                    <td class="select-column">
                      <input
                        type="checkbox"
                        :value="String(dealer.id)"
                        v-model="selectedDealerIds"
                        :disabled="saving || !canSelectDealer(dealer)"
                      />
                    </td>

                    <td class="primary-cell dealer-name-cell">
                      <div class="dealer-name-wrap">
                        <strong :title="dealer.name">
                          {{ dealer.name }}
                        </strong>

                        <span v-if="dealer.is_existing" class="existing-badge">
                          Existing
                        </span>
                      </div>
                    </td>

                    <td>
                      <span class="route-cell" :title="dealer.route_name || '-'">
                        {{ dealer.route_name || "-" }}
                      </span>
                    </td>

                    <td class="address-cell" :title="dealer.address || '-'">
                      {{ dealer.address || "-" }}
                    </td>

                    <td>
                      <span
                        class="availability-pill"
                        :class="getAvailabilityClass(dealer)"
                      >
                        {{ getAvailabilityLabel(dealer) }}
                      </span>
                    </td>

                    <td
                      class="existing-schedules-cell"
                      :title="dealer.schedule_names.join(', ')"
                    >
                      <div
                        v-if="dealer.schedule_names.length"
                        class="schedule-chip-list"
                      >
                        <span
                          v-for="scheduleName in dealer.schedule_names"
                          :key="scheduleName"
                          class="schedule-name-chip"
                          :title="scheduleName"
                        >
                          {{ scheduleName }}
                        </span>
                      </div>

                      <span v-else class="no-schedule-text">
                        No schedules
                      </span>
                    </td>
                  </tr>

                  <tr v-if="!filteredDealerList.length">
                    <td colspan="6" class="empty-table">
                      No dealers found for this schedule.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- =====================================================
             ACTION BAR
        ====================================================== -->
        <div class="action-bar">
          <button
            type="button"
            class="btn cancel-button"
            :disabled="saving"
            @click="goBack"
          >
            Cancel
          </button>

          <button
            type="button"
            class="btn save-button"
            :disabled="saving"
            @click="saveDealerList"
          >
            <span v-if="saving" class="spinner-border spinner-border-sm"></span>

            <span v-else>Update Schedule</span>
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { useToast } from "vue-toastification";

import {
  fetchDeliveryScheduleById,
  updateDeliverySchedule,
} from "@/services/dataService";

export default {
  name: "DeliveryScheduleEditData",

  data() {
    return {
      loading: false,
      saving: false,

      token: localStorage.getItem("token_set"),
      scheduleId: "",
      dealerSearchText: "",

      schedule: {
        id: "",
        name: "",
        distributor_id: "",
        distributor_name: "",
        route_name: "",
        vehicle_name: "",
        time_slot_id: "",
        time_slot_label: "",
        effective_from: "",
        effective_to: "",
        schedule_type: "",
        schedule_type_label: "",
        schedule_text: "",
        remarks: "",
        status: "active",
      },

      dealerList: [],
      existingDealerIds: [],
      selectedDealerIds: [],
    };
  },

  setup() {
    const toast = useToast();

    return {
      toast,
    };
  },

  computed: {
    isScheduleActive() {
      return this.schedule.status === "active";
    },

    filteredDealerList() {
      const keyword = this.dealerSearchText.trim().toLowerCase();

      if (!keyword) {
        return this.dealerList;
      }

      return this.dealerList.filter((dealer) => {
        return (
          String(dealer.name || "").toLowerCase().includes(keyword) ||
          String(dealer.route_name || "").toLowerCase().includes(keyword) ||
          String(dealer.address || "").toLowerCase().includes(keyword) ||
          this.getAvailabilityLabel(dealer).toLowerCase().includes(keyword) ||
          dealer.schedule_names.join(" ").toLowerCase().includes(keyword)
        );
      });
    },

    selectableFilteredDealerList() {
      return this.filteredDealerList.filter((dealer) =>
        this.canSelectDealer(dealer),
      );
    },

    isAllDealersSelected() {
      return (
        this.selectableFilteredDealerList.length > 0 &&
        this.selectableFilteredDealerList.every((dealer) =>
          this.selectedDealerIds.includes(String(dealer.id)),
        )
      );
    },
  },

  mounted() {
    this.scheduleId = this.$route.params.id || "";
    this.loadScheduleForEdit();
  },

  methods: {
    bodyOf(response) {
      return response && response.data ? response.data : response;
    },

    isSuccessResponse(body) {
      if (!body) {
        return false;
      }

      if (body.status === undefined) {
        return true;
      }

      const status = String(body.status).toLowerCase();

      return (
        status === "success" ||
        status === "true" ||
        status === "1" ||
        body.status === true ||
        body.success === true
      );
    },

    dataOf(body) {
      if (!body) {
        return null;
      }

      if (body.data) {
        return body.data;
      }

      if (body.Data) {
        return body.Data;
      }

      return body;
    },

    async loadScheduleForEdit() {
      if (!this.scheduleId) {
        this.toast.error("Delivery schedule id is missing.");
        this.goBack();
        return;
      }

      this.loading = true;

      try {
        const response = await fetchDeliveryScheduleById(
          this.scheduleId,
          this.token,
        );

        const body = this.bodyOf(response);

        if (!this.isSuccessResponse(body)) {
          this.toast.error(
            body && body.message
              ? body.message
              : "Failed to load delivery schedule.",
          );

          this.goBack();
          return;
        }

        const scheduleData = this.dataOf(body);

        if (!scheduleData || !scheduleData.id) {
          this.toast.error("Delivery schedule not found.");
          this.goBack();
          return;
        }

        this.schedule = this.mapScheduleRow(scheduleData);
        this.dealerList = this.buildDealerList(scheduleData);
        this.selectedDealerIds = [...this.existingDealerIds];

        if (!this.dealerList.length) {
          this.toast.error("No dealers found for this schedule.");
        }
      } catch (error) {
        console.error("Delivery schedule edit load error:", error);
        console.error("Delivery schedule edit API response:", error.response);

        const message =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : "Failed to load delivery schedule.";

        this.toast.error(message);
      } finally {
        this.loading = false;
      }
    },

    mapScheduleRow(row) {
      const active =
        row.is_active === true ||
        row.is_active === 1 ||
        row.is_active === "1";

      return {
        id: String(row.id || ""),
        name: row.name || `Schedule #${row.id || ""}`,
        distributor_id: String(row.distributor_profile_id || ""),
        distributor_name: row.distributor_name || "-",
        route_name: row.routes || row.route_name || "-",
        vehicle_name: row.vehicles || row.vehicle_name || "-",
        time_slot_id: String(row.time_slot_id || ""),
        time_slot_label: row.time_slot || row.time_slot_label || "-",
        effective_from: row.start_date || row.effective_from || "",
        effective_to: row.end_date || row.effective_to || "",
        schedule_type: row.recurrence_type || row.schedule_type || "",
        schedule_type_label: this.getFrequencyLabel(
          row.recurrence_type || row.schedule_type,
        ),
        schedule_text: this.buildScheduleText(row),
        remarks: row.remarks || "",
        status: active ? "active" : "inactive",
      };
    },

    buildDealerList(scheduleData) {
      const existingRows = Array.isArray(scheduleData.existing_dealers)
        ? scheduleData.existing_dealers
        : [];

      const dealerRows = Array.isArray(scheduleData.dealers)
        ? scheduleData.dealers
        : [];

      const existingMap = new Map();
      const existingOrderMap = new Map();

      existingRows.forEach((dealer, index) => {
        const dealerId = this.getDealerId(dealer);

        if (dealerId) {
          existingMap.set(String(dealerId), dealer);
          existingOrderMap.set(String(dealerId), index);
        }
      });

      this.existingDealerIds = Array.from(existingMap.keys());

      const dealerMap = new Map();

      dealerRows.forEach((dealer, index) => {
        const dealerId = this.getDealerId(dealer);
        const mapKey = dealerId ? String(dealerId) : `missing-${index}`;
        const isExisting = dealerId ? existingMap.has(String(dealerId)) : false;

        dealerMap.set(
          mapKey,
          this.mapDealerRow(dealer, index, isExisting, existingOrderMap),
        );
      });

      existingRows.forEach((dealer, index) => {
        const dealerId = this.getDealerId(dealer);

        if (!dealerId || dealerMap.has(String(dealerId))) {
          return;
        }

        dealerMap.set(
          String(dealerId),
          this.mapDealerRow(
            {
              ...dealer,
              is_scheduled: 1,
              schedules: [
                {
                  schedule_id: scheduleData.id,
                  name: scheduleData.name,
                },
              ],
            },
            index,
            true,
            existingOrderMap,
          ),
        );
      });

      return Array.from(dealerMap.values()).sort((a, b) => {
        if (a.is_existing !== b.is_existing) {
          return a.is_existing ? -1 : 1;
        }

        if (a.is_existing && b.is_existing) {
          return a.existing_order - b.existing_order;
        }

        const routeCompare = String(a.route_name || "").localeCompare(
          String(b.route_name || ""),
        );

        if (routeCompare !== 0) {
          return routeCompare;
        }

        return String(a.name || "").localeCompare(String(b.name || ""));
      });
    },

    getDealerId(dealer) {
      return (
        dealer.dealer_profile_id ||
        dealer.profile_id ||
        dealer.dealer_id ||
        dealer.id ||
        ""
      );
    },

    mapDealerRow(
      dealer,
      index,
      isExisting = false,
      existingOrderMap = new Map(),
    ) {
      const dealerId = this.getDealerId(dealer);

      const name =
        dealer.business_display_name ||
        dealer.business_name ||
        dealer.dealer_name ||
        dealer.name ||
        dealer.profile_name ||
        `Dealer ${index + 1}`;

      const schedules = this.normalizeSchedules(dealer.schedules);

      if (isExisting && !schedules.length) {
        schedules.push({
          schedule_id: this.schedule.id || this.scheduleId,
          name: this.schedule.name || `Schedule #${this.scheduleId}`,
        });
      }

      const isScheduled =
        dealer.is_scheduled === true ||
        dealer.is_scheduled === 1 ||
        dealer.is_scheduled === "1" ||
        schedules.length > 0;

      return {
        ...dealer,
        id: dealerId ? String(dealerId) : "",
        row_key: dealerId
          ? `dealer-${dealerId}`
          : `dealer-missing-id-${index}`,
        name,
        route_name: dealer.route || dealer.route_name || "-",
        address: dealer.address || dealer.address1 || dealer.area || "-",
        is_existing: isExisting,
        existing_order:
          dealerId && existingOrderMap.has(String(dealerId))
            ? existingOrderMap.get(String(dealerId))
            : 999999,
        is_scheduled: isScheduled,
        schedules,
        schedule_names: schedules
          .map((schedule) => schedule.name || schedule.schedule_name || "")
          .filter((item) => item),
      };
    },

    normalizeSchedules(schedules) {
      if (!Array.isArray(schedules)) {
        return [];
      }

      return schedules
        .map((schedule) => ({
          schedule_id:
            schedule.schedule_id ||
            schedule.id ||
            schedule.delivery_schedule_id ||
            "",
          name:
            schedule.name ||
            schedule.schedule_name ||
            schedule.delivery_schedule_name ||
            (schedule.schedule_id ? `Schedule #${schedule.schedule_id}` : ""),
        }))
        .filter((schedule) => schedule.schedule_id || schedule.name);
    },

    canSelectDealer(dealer) {
      if (!dealer || !dealer.id) {
        return false;
      }

      if (dealer.is_existing) {
        return true;
      }

      return !dealer.is_scheduled;
    },

    getAvailabilityLabel(dealer) {
      if (!dealer) {
        return "-";
      }

      return dealer.is_scheduled ? "Not Available" : "Available";
    },

    getAvailabilityClass(dealer) {
      if (!dealer) {
        return "";
      }

      return dealer.is_scheduled ? "not-available" : "available";
    },

    parseCsvList(value) {
      if (Array.isArray(value)) {
        return value
          .map((item) => String(item).trim())
          .filter((item) => item);
      }

      if (value === null || value === undefined || value === "") {
        return [];
      }

      return String(value)
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item);
    },

    buildScheduleText(row) {
      const recurrenceType = String(
        row.recurrence_type || row.schedule_type || "",
      ).toLowerCase();

      const monthText = this.parseCsvList(row.months)
        .map((month) => this.getMonthLabel(month))
        .join(", ");

      const weekText = this.parseCsvList(row.weeks)
        .map((week) => this.getWeekLabel(week))
        .join(", ");

      const weekdayText = this.parseCsvList(row.weekdays)
        .map((day) => this.getDayLabel(day))
        .join(", ");

      const parts = [];

      if (recurrenceType === "month" || recurrenceType === "month_by_month") {
        if (monthText) {
          parts.push(monthText);
        }

        if (weekText) {
          parts.push(weekText);
        }

        if (weekdayText) {
          parts.push(weekdayText);
        }
      } else if (
        recurrenceType === "week" ||
        recurrenceType === "week_by_week"
      ) {
        if (weekText) {
          parts.push(weekText);
        }

        if (weekdayText) {
          parts.push(weekdayText);
        }
      } else {
        if (weekdayText) {
          parts.push(weekdayText);
        }
      }

      return parts.length ? parts.join(" • ") : "-";
    },

    getDayLabel(value) {
      const cleanValue = String(value || "").trim().toLowerCase();

      const dayMap = {
        "1": "Mon",
        "2": "Tue",
        "3": "Wed",
        "4": "Thu",
        "5": "Fri",
        "6": "Sat",
        "7": "Sun",
        monday: "Mon",
        mon: "Mon",
        tuesday: "Tue",
        tue: "Tue",
        wednesday: "Wed",
        wed: "Wed",
        thursday: "Thu",
        thu: "Thu",
        friday: "Fri",
        fri: "Fri",
        saturday: "Sat",
        sat: "Sat",
        sunday: "Sun",
        sun: "Sun",
      };

      return dayMap[cleanValue] || value;
    },

    getWeekLabel(value) {
      const cleanValue = String(value || "").trim();

      if (!cleanValue) {
        return "";
      }

      if (cleanValue.toLowerCase().startsWith("week")) {
        return cleanValue;
      }

      return `Week ${cleanValue}`;
    },

    getMonthLabel(value) {
      const cleanValue = String(value || "").trim().toLowerCase();

      const monthMap = {
        "1": "Jan",
        "2": "Feb",
        "3": "Mar",
        "4": "Apr",
        "5": "May",
        "6": "Jun",
        "7": "Jul",
        "8": "Aug",
        "9": "Sep",
        "10": "Oct",
        "11": "Nov",
        "12": "Dec",
        january: "Jan",
        february: "Feb",
        march: "Mar",
        april: "Apr",
        may: "May",
        june: "Jun",
        july: "Jul",
        august: "Aug",
        september: "Sep",
        october: "Oct",
        november: "Nov",
        december: "Dec",
      };

      return monthMap[cleanValue] || value;
    },

    toggleScheduleStatus() {
      if (this.saving) {
        return;
      }

      this.schedule.status =
        this.schedule.status === "active" ? "inactive" : "active";
    },

    toggleAllDealers(event) {
      const checked = event.target.checked;

      const visibleIds = this.selectableFilteredDealerList.map((dealer) =>
        String(dealer.id),
      );

      if (checked) {
        this.selectedDealerIds = Array.from(
          new Set([...this.selectedDealerIds, ...visibleIds]),
        );
      } else {
        this.selectedDealerIds = this.selectedDealerIds.filter((id) => {
          return !visibleIds.includes(String(id));
        });
      }
    },

    selectAllVisibleDealers() {
      const visibleIds = this.selectableFilteredDealerList.map((dealer) =>
        String(dealer.id),
      );

      this.selectedDealerIds = Array.from(
        new Set([...this.selectedDealerIds, ...visibleIds]),
      );
    },

    clearSelectedDealers() {
      this.selectedDealerIds = [];
    },

    async saveDealerList() {
      if (!this.selectedDealerIds.length) {
        this.toast.error("Please select at least one dealer.");
        return;
      }

      this.saving = true;

      try {
        const payload = {
          dealer_profile_ids: this.selectedDealerIds.map((id) => Number(id)),
          is_active: this.isScheduleActive ? 1 : 0,
        };

        console.log("Delivery Schedule Update Payload:", payload);

        const response = await updateDeliverySchedule(
          this.scheduleId,
          payload,
          this.token,
        );

        const body = this.bodyOf(response);

        if (!this.isSuccessResponse(body)) {
          this.toast.error(
            body && body.message
              ? body.message
              : "Failed to update delivery schedule.",
          );
          return;
        }

        this.toast.success(
          body && body.message
            ? body.message
            : "Delivery schedule updated successfully.",
        );

        this.goBack();
      } catch (error) {
        console.error("Delivery schedule update error:", error);
        console.error("Delivery schedule update API response:", error.response);

        const message =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : "Failed to update delivery schedule.";

        this.toast.error(message);
      } finally {
        this.saving = false;
      }
    },

    goBack() {
      this.$router.push({
        path: "/delivery-schedule-history",
      });
    },

    formatDate(value) {
      if (!value) {
        return "-";
      }

      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return value;
      }

      return date.toLocaleDateString("en-GB", {
        year: "numeric",
        month: "short",
        day: "2-digit",
      });
    },

    getFrequencyLabel(type) {
      const normalizedType = String(type || "").toLowerCase();

      if (normalizedType === "day" || normalizedType === "day_by_day") {
        return "Day by Day";
      }

      if (normalizedType === "week" || normalizedType === "week_by_week") {
        return "Week By Week";
      }

      if (normalizedType === "month" || normalizedType === "month_by_month") {
        return "Month By Month";
      }

      return "-";
    },

    getBatchTypeClass(type) {
      const normalizedType = String(type || "").toLowerCase();

      if (normalizedType === "day" || normalizedType === "day_by_day") {
        return "day-type";
      }

      if (normalizedType === "week" || normalizedType === "week_by_week") {
        return "week-type";
      }

      if (normalizedType === "month" || normalizedType === "month_by_month") {
        return "month-type";
      }

      return "";
    },
  },
};
</script>

<style scoped>
.delivery-edit-page {
  padding-bottom: 24px;
}

.edit-container {
  width: calc(100% - 20px);
  margin: 10px;
  padding: 14px;
  box-sizing: border-box;
  background: #ffffff;
  border-radius: 8px;
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.03),
    0 4px 18px rgba(15, 23, 42, 0.06);
}

/* =========================================================
   HEADER
========================================================= */

.page-title-card {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 7px;
  background: linear-gradient(180deg, #ffffff 0%, #f9fafb 100%);
}

.page-title-card h5 {
  margin: 0;
  color: #111827;
  font-size: 14px;
  font-weight: 900;
}

.page-title-card span {
  display: block;
  margin-top: 3px;
  color: #6b7280;
  font-size: 11px;
}

.back-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 30px;
  padding: 5px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #ffffff;
  color: #374151;
  font-size: 11px;
  font-weight: 900;
  cursor: pointer;
}

.back-button:hover {
  background: #f9fafb;
}

/* =========================================================
   COMMON SECTION
========================================================= */

.section-card {
  position: relative;
  margin-bottom: 12px;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 7px;
  background: #ffffff;
}

.section-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.section-header h6 {
  margin: 0;
  color: #111827;
  font-size: 13px;
  font-weight: 800;
}

.section-header span {
  display: block;
  margin-top: 2px;
  color: #6b7280;
  font-size: 11px;
}

.section-count {
  flex-shrink: 0;
  padding: 4px 8px;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  background: #f3f4f6;
  color: #374151;
  font-size: 10px;
  font-weight: 800;
}

.loading-card {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  min-height: 130px;
  border: 1px dashed #d1d5db;
  border-radius: 7px;
  background: #fafafa;
  color: #6b7280;
  font-size: 12px;
  font-weight: 800;
}

/* =========================================================
   READONLY DETAILS
========================================================= */

.details-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(145px, 1fr));
  gap: 8px;
}

.readonly-field {
  min-width: 0;
}

.readonly-field.wide-field {
  grid-column: span 2;
}

.readonly-field label {
  display: block;
  margin-bottom: 4px;
  color: #374151;
  font-size: 11px;
  font-weight: 800;
}

.readonly-value {
  min-height: 32px;
  padding: 6px 8px;
  box-sizing: border-box;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #f9fafb;
  color: #111827;
  font-size: 11.8px;
  font-weight: 800;
}

.readonly-long-value {
  max-height: 52px;
  overflow: auto;
  line-height: 1.35;
  white-space: normal;
}

.route-value {
  color: #111827;
}

/* =========================================================
   DATE PREVIEW
========================================================= */

.date-preview-card {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 10px;
  border: 1px solid #e5e7eb;
  border-radius: 7px;
  background: #fafafa;
}

.date-preview-icon {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border-radius: 8px;
  background: #f3f4f6;
  color: #374151;
}

.date-preview-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.date-preview-content strong {
  margin-top: 3px;
  color: #111827;
  font-size: 12px;
  font-weight: 900;
}

.date-preview-content small {
  margin-top: 2px;
  color: #374151;
  font-size: 10px;
  font-weight: 800;
}

.batch-type {
  width: fit-content;
  padding: 2px 6px;
  border-radius: 999px;
  font-size: 8px;
  font-weight: 900;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.batch-type.day-type {
  background: #dcfce7;
  color: #166534;
}

.batch-type.week-type {
  background: #f3f4f6;
  color: #374151;
}

.batch-type.month-type {
  background: #f3e8ff;
  color: #7e22ce;
}

/* =========================================================
   FORM
========================================================= */

.form-item {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.form-item label {
  display: block;
  margin-bottom: 4px;
  color: #374151;
  font-size: 11px;
  font-weight: 800;
}

.search-input-wrap {
  position: relative;
}

.search-input-wrap > i {
  position: absolute;
  top: 50%;
  left: 10px;
  z-index: 2;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 11px;
  pointer-events: none;
}

.search-input-wrap input {
  width: 100%;
  height: 32px;
  min-height: 32px;
  padding: 5px 9px 5px 29px;
  box-sizing: border-box;
  border: 1px solid #d1d5db;
  border-radius: 5px;
  outline: none;
  color: #111827;
  font-size: 12px;
}

.search-input-wrap input:focus {
  border-color: #9ca3af;
  box-shadow: 0 0 0 2px rgba(107, 114, 128, 0.1);
}

.dealer-filter-grid {
  display: grid;
  grid-template-columns: minmax(260px, 1fr);
  gap: 10px;
  align-items: end;
  margin-bottom: 9px;
}

/* =========================================================
   TABLE
========================================================= */

.selection-table-card {
  overflow: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #ffffff;
}

.selection-table-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 7px 9px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.toolbar-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.toolbar-title strong {
  color: #111827;
  font-size: 12px;
}

.toolbar-title span {
  color: #6b7280;
  font-size: 10px;
  font-weight: 700;
}

.toolbar-actions {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.mini-action-button {
  height: 24px;
  padding: 0 8px;
  border: 1px solid #d1d5db;
  border-radius: 999px;
  background: #f9fafb;
  color: #374151;
  font-size: 9.8px;
  font-weight: 900;
  cursor: pointer;
}

.mini-action-button.clear {
  border-color: #e5e7eb;
  background: #ffffff;
  color: #6b7280;
}

.mini-action-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.table-scroll {
  overflow: auto;
  scrollbar-gutter: stable;
}

.dealer-table-scroll {
  max-height: 430px;
}

.selection-table {
  width: 100%;
  margin: 0;
  border-collapse: separate;
  border-spacing: 0;
}

.dealer-table {
  min-width: 1080px;
}

.selection-table th,
.selection-table td {
  padding: 7px 8px;
  border-right: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
  color: #111827;
  font-size: 11.7px;
  white-space: nowrap;
}

.selection-table thead th {
  position: sticky;
  top: 0;
  z-index: 4;
  background: #f9fafb;
  color: #374151;
  font-weight: 800;
}

.selection-table tbody tr:hover td {
  background: #f8fafc;
}

.selection-table tbody tr.selected td {
  background: #f3f4f6;
}

.selection-table tbody tr.existing-row td {
  background: #f8fafc;
}

.selection-table tbody tr.selected.existing-row td {
  background: #eef2f7;
}

.selection-table tbody tr.disabled-row td {
  color: #6b7280;
}

.select-column {
  width: 42px;
  text-align: center;
}

.primary-cell {
  font-weight: 800;
}

.dealer-name-cell {
  min-width: 220px;
}

.dealer-name-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.dealer-name-wrap strong {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.existing-badge {
  flex-shrink: 0;
  padding: 2px 6px;
  border-radius: 999px;
  background: #e0f2fe;
  color: #0369a1;
  font-size: 8.8px;
  font-weight: 900;
}

.route-cell {
  display: inline-block;
  max-width: 170px;
  overflow: hidden;
  color: #374151;
  font-size: 11px;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.address-cell {
  max-width: 300px;
  overflow: hidden;
  color: #374151;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap !important;
}

.existing-schedules-cell {
  min-width: 260px;
  max-width: 420px;
  white-space: normal !important;
}

.schedule-chip-list {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 5px;
  width: 100%;
  max-width: 410px;
  max-height: 76px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 3px;
}

.schedule-name-chip {
  display: inline-flex;
  align-items: center;
  max-width: 180px;
  overflow: hidden;
  padding: 3px 7px;
  border: 1px solid #fed7aa;
  border-radius: 999px;
  background: #fff7ed;
  color: #c2410c;
  font-size: 9.5px;
  font-weight: 900;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.no-schedule-text {
  color: #9ca3af;
  font-size: 10px;
  font-weight: 800;
}

.availability-pill {
  display: inline-block;
  min-width: 82px;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 900;
  text-align: center;
}

.availability-pill.available {
  background: #dcfce7;
  color: #166534;
}

.availability-pill.not-available {
  background: #fee2e2;
  color: #991b1b;
}

.empty-table {
  padding: 22px 10px !important;
  text-align: center;
  color: #6b7280 !important;
}

/* =========================================================
   STATUS + SCHEDULE TOGGLE
========================================================= */

.schedule-status-box {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  min-width: 285px;
  padding: 8px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fafafa;
}

.schedule-status-text {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.status-dot {
  width: 9px;
  height: 9px;
  flex-shrink: 0;
  border-radius: 50%;
  background: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.12);
}

.status-dot.active {
  background: #22c55e;
  box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.12);
}

.schedule-status-text strong {
  display: block;
  color: #111827;
  font-size: 11.5px;
  font-weight: 900;
  line-height: 1.1;
}

.schedule-status-text small {
  display: block;
  margin-top: 2px;
  color: #6b7280;
  font-size: 9.8px;
  font-weight: 700;
  line-height: 1.1;
}

.schedule-switch {
  position: relative;
  display: block;
  width: 62px;
  height: 28px;
  flex-shrink: 0;
  padding: 0;
  border: none;
  border-radius: 999px;
  background: #ef4444;
  cursor: pointer;
  overflow: hidden;
  line-height: 0;
  box-sizing: border-box;
  transition:
    background 0.2s ease,
    opacity 0.2s ease,
    box-shadow 0.2s ease;
}

.schedule-switch.active {
  background: #22c55e;
}

.schedule-switch:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.switch-knob {
  position: absolute;
  top: 4px;
  left: 4px;
  z-index: 2;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 2px 5px rgba(15, 23, 42, 0.25);
  transform: translateX(0);
  transition: transform 0.2s ease;
}

.schedule-switch.active .switch-knob {
  transform: translateX(34px);
}

.switch-label {
  position: absolute;
  top: 42%;
  right: 8px;
  z-index: 1;
  transform: translateY(-50%);
  color: #ffffff !important;
  font-size: 8.5px;
  font-weight: 900;
  letter-spacing: 0.04em;
  line-height: 1;
  pointer-events: none;
}

.schedule-switch.active .switch-label {
  right: auto;
  left: 8px;
  color: #ffffff !important;
}

.switch-knob {
  position: absolute;
  top: 42%;
  left: 4px;
  z-index: 2;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 2px 5px rgba(15, 23, 42, 0.25);
  transform: translateY(-50%);
  transition: transform 0.2s ease;
}

.schedule-switch.active .switch-knob {
  transform: translate(34px, -50%);
}

/* =========================================================
   ACTION BAR
========================================================= */

.action-bar {
  position: sticky;
  bottom: 0;
  z-index: 20;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 0 0;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #ffffff 28%);
}

.btn {
  height: 34px;
  min-height: 34px;
  padding: 6px 13px;
  border: none;
  border-radius: 5px;
  color: #ffffff;
  font-size: 12.5px;
  font-weight: 800;
  white-space: nowrap;
  cursor: pointer;
}

.btn:disabled {
  background: #6c757d !important;
  cursor: not-allowed;
  opacity: 0.7;
}

.save-button {
  min-width: 150px;
  background: #28a745;
}

.cancel-button {
  min-width: 90px;
  background: #6c757d;
}

/* =========================================================
   SCROLLBARS
========================================================= */

.delivery-edit-page,
.delivery-edit-page * {
  scrollbar-width: thin;
  scrollbar-color: rgba(100, 116, 139, 0.34) transparent;
}

.delivery-edit-page *::-webkit-scrollbar {
  width: 5px;
  height: 5px;
}

.delivery-edit-page *::-webkit-scrollbar-track {
  background: transparent;
}

.delivery-edit-page *::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: rgba(100, 116, 139, 0.27);
}

.delivery-edit-page *::-webkit-scrollbar-thumb:hover {
  background: rgba(100, 116, 139, 0.55);
}

.delivery-edit-page *::-webkit-scrollbar-corner {
  background: transparent;
}

/* =========================================================
   RESPONSIVE
========================================================= */

@media (max-width: 1200px) {
  .details-grid {
    grid-template-columns: repeat(3, minmax(150px, 1fr));
  }

  .readonly-field.wide-field {
    grid-column: span 3;
  }

  .existing-schedules-cell {
    min-width: 230px;
    max-width: 340px;
  }

  .schedule-chip-list {
    max-width: 330px;
    max-height: 86px;
  }

  .schedule-name-chip {
    max-width: 150px;
  }
}

@media (max-width: 900px) {
  .edit-container {
    width: calc(100% - 12px);
    margin: 6px;
    padding: 12px;
  }

  .page-title-card {
    flex-direction: column;
  }

  .back-button {
    width: 100%;
  }

  .details-grid,
  .dealer-filter-grid {
    grid-template-columns: 1fr;
  }

  .readonly-field.wide-field {
    grid-column: span 1;
  }

  .schedule-status-box {
    width: 100%;
  }

  .action-bar {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .action-bar .btn {
    width: 100%;
  }

  .selection-table-toolbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .toolbar-actions {
    width: 100%;
    justify-content: flex-end;
  }
}

@media (max-width: 700px) {
  .existing-schedules-cell {
    min-width: 210px;
    max-width: 280px;
  }

  .schedule-chip-list {
    max-width: 270px;
    max-height: 92px;
  }

  .schedule-name-chip {
    max-width: 128px;
    padding: 3px 6px;
    font-size: 9px;
  }
}

@media (max-width: 520px) {
  .section-header,
  .dealer-section-header {
    flex-direction: column;
    gap: 8px;
  }

  .section-count {
    width: fit-content;
  }

  .toolbar-actions {
    width: 100%;
  }

  .mini-action-button {
    flex: 1;
  }

  .action-bar {
    grid-template-columns: 1fr;
  }
}
</style>